import type { PlayerController } from '../player/controller';
import type { FollowCamera } from '../scene/camera';

export function isTouchDevice(): boolean {
  return (
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0)
  );
}

type TouchUIHandle = {
  el: HTMLDivElement;
  destroy: () => void;
};

// Mounts an on-screen joystick (left thumb), drag-look (right half of the
// screen), and big TAG / HOOD buttons. Wires straight into PlayerController
// (externalAxis) and FollowCamera.yaw/pitch.
export function mountTouchUI(
  parent: HTMLElement,
  controller: PlayerController,
  camera: FollowCamera,
): TouchUIHandle {
  const root = document.createElement('div');
  root.className = 'ui interactive';
  root.style.cssText = `
    position: fixed; inset: 0; pointer-events: none; z-index: 50;
    user-select: none; touch-action: none;
  `;
  parent.appendChild(root);

  // ---- Joystick (left bottom) ----
  const stickWrap = document.createElement('div');
  stickWrap.style.cssText = `
    position: fixed; left: 24px; bottom: 24px;
    width: 130px; height: 130px;
    border-radius: 50%;
    background: rgba(255,255,255,.08);
    border: 2px solid rgba(255,255,255,.18);
    pointer-events: auto;
    touch-action: none;
  `;
  const knob = document.createElement('div');
  knob.style.cssText = `
    position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
    width: 56px; height: 56px; border-radius: 50%;
    background: rgba(255,255,255,.35);
    pointer-events: none;
  `;
  stickWrap.appendChild(knob);
  root.appendChild(stickWrap);

  let stickPointerId: number | null = null;
  let stickCenter = { x: 0, y: 0 };
  const STICK_RADIUS = 50;

  const updateStick = (x: number, y: number) => {
    const dx = x - stickCenter.x;
    const dy = y - stickCenter.y;
    const d = Math.hypot(dx, dy);
    const k = d > STICK_RADIUS ? STICK_RADIUS / d : 1;
    const cx = dx * k;
    const cy = dy * k;
    knob.style.transform = `translate(calc(-50% + ${cx}px), calc(-50% + ${cy}px))`;
    controller.externalAxis = {
      x: cx / STICK_RADIUS,
      y: -cy / STICK_RADIUS,
    };
  };

  const resetStick = () => {
    stickPointerId = null;
    knob.style.transform = 'translate(-50%, -50%)';
    controller.externalAxis = null;
  };

  stickWrap.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    stickWrap.setPointerCapture(e.pointerId);
    stickPointerId = e.pointerId;
    const rect = stickWrap.getBoundingClientRect();
    stickCenter = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    updateStick(e.clientX, e.clientY);
  });
  stickWrap.addEventListener('pointermove', (e) => {
    if (stickPointerId !== e.pointerId) return;
    updateStick(e.clientX, e.clientY);
  });
  const stickEnd = (e: PointerEvent) => {
    if (stickPointerId !== e.pointerId) return;
    resetStick();
  };
  stickWrap.addEventListener('pointerup', stickEnd);
  stickWrap.addEventListener('pointercancel', stickEnd);

  // ---- Action buttons (right bottom) ----
  const buttons = document.createElement('div');
  buttons.style.cssText = `
    position: fixed; right: 24px; bottom: 24px;
    display: grid; grid-template-columns: repeat(2, auto); gap: 12px;
    pointer-events: auto;
  `;
  const makeButton = (
    label: string,
    color: string,
    action: 'tag' | 'hood' | 'interact' | 'use1',
    size = 78,
  ) => {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.cssText = `
      width: ${size}px; height: ${size}px; border-radius: 50%;
      background: ${color}; color: #0b0d12; border: 0;
      font: 800 14px system-ui, sans-serif; letter-spacing: .5px;
      box-shadow: 0 6px 18px rgba(0,0,0,.45);
      touch-action: none;
    `;
    b.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('smp:action', { detail: action }));
      b.style.transform = 'scale(.92)';
    });
    b.addEventListener('pointerup', () => { b.style.transform = ''; });
    b.addEventListener('pointercancel', () => { b.style.transform = ''; });
    return b;
  };
  const useBtn = makeButton('USE', '#86efac', 'use1');
  const interactBtn = makeButton('E', '#fde047', 'interact');
  const hoodBtn = makeButton('HOOD', '#60a5fa', 'hood');
  const tagBtn = makeButton('TAG', '#fb923c', 'tag', 92);
  buttons.append(useBtn, interactBtn, hoodBtn, tagBtn);
  root.appendChild(buttons);

  // ---- Drag-to-look (right half of screen, outside button) ----
  const lookOverlay = document.createElement('div');
  lookOverlay.style.cssText = `
    position: fixed; top: 0; right: 0; width: 50%; height: 100%;
    pointer-events: auto; touch-action: none;
  `;
  root.appendChild(lookOverlay);

  let lookPointerId: number | null = null;
  let lastLook = { x: 0, y: 0 };
  lookOverlay.addEventListener('pointerdown', (e) => {
    if (lookPointerId !== null) return;
    lookOverlay.setPointerCapture(e.pointerId);
    lookPointerId = e.pointerId;
    lastLook = { x: e.clientX, y: e.clientY };
  });
  lookOverlay.addEventListener('pointermove', (e) => {
    if (lookPointerId !== e.pointerId) return;
    const dx = e.clientX - lastLook.x;
    const dy = e.clientY - lastLook.y;
    lastLook = { x: e.clientX, y: e.clientY };
    camera.yaw -= dx * 0.005;
    camera.pitch -= dy * 0.0035;
  });
  const lookEnd = (e: PointerEvent) => {
    if (lookPointerId !== e.pointerId) return;
    lookPointerId = null;
  };
  lookOverlay.addEventListener('pointerup', lookEnd);
  lookOverlay.addEventListener('pointercancel', lookEnd);

  return {
    el: root,
    destroy() {
      root.remove();
    },
  };
}
